import React, { useEffect, useRef, useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import { deleteSavedIncentiveList, downloadSavedIncentiveList, fetchSavedIncentiveLists, SavedIncentiveList, updateSavedIncentiveListName } from "../api/incentiveListApi";
import { downloadBlob } from "../utils/downloadBlob";
import { notifications } from "@mantine/notifications";
import { ActionIcon, Anchor, Box, Button, Center, Group, Loader, ScrollArea, Stack, Table, Text, TextInput, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { FlagIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { formatDate } from "../utils/dateFormat";
import { BaseModal } from "./BaseModal";
import IncentiveListSavedDetailModal from "./IncentiveListSavedDetailModal";
import { confirmDelete } from "../utils/confirm";

interface Props {
  client: Client
}

const IncentiveListSavedPage: React.FC<Props> = ({ client }) => {
  const swrKey = `incentive-lists-saved-${client.id}`
  // 一覧は更新され得るため revalidate する useSWR を使う。
  // useSWRImmutable はマウント時に再検証しないため、DL後にページ遷移しても古い一覧のままになる。
  const { data, error, mutate } = useSWR<SavedIncentiveList[]>(
    swrKey,
    () => fetchSavedIncentiveLists(client.id),
  )

  // ファイル名編集モーダルの状態（編集対象の行・入力中の名前・保存中フラグ）
  const [editingRow, setEditingRow] = useState<SavedIncentiveList | null>(null)
  const [editingName, setEditingName] = useState('')
  const [saving, setSaving] = useState(false)

  // 詳細モーダルの状態。閉じるアニメーション中も内容を保つため opened と対象行を別に持つ
  const [detailRow, setDetailRow] = useState<SavedIncentiveList | null>(null)
  const [detailOpened, setDetailOpened] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editingRow !== null) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
  }, [editingRow])

  const handleShowDetail = (row: SavedIncentiveList) => {
    setDetailRow(row)
    setDetailOpened(true)
  }

  const handleDownload = async (row: SavedIncentiveList) => {
    try {
      const blob = await downloadSavedIncentiveList(client.id, row.id)
      downloadBlob(blob, `${row.name}.xlsx`)
    } catch (e) {
      console.error('特典付与リストのダウンロードに失敗しました', e)
      notifications.show({ message: 'ダウンロードに失敗しました', color: 'red' })
    }
  }

  // 編集：モーダルを開く
  const openRenameModal = (row: SavedIncentiveList) => {
    setEditingRow(row)
    setEditingName(row.name)
  }

  const closeRenameModal = () => {
    if (saving) return
    setEditingRow(null)
    setEditingName('')
  }

  // モーダルでリスト名（ファイル名）を保存する
  const handleRenameSubmit = async () => {
    if (editingRow === null) return
    const trimmed = editingName.trim()
    if (trimmed === '') return
    if (trimmed === editingRow.name) {
      closeRenameModal()
      return
    }
    setSaving(true)
    try {
      await updateSavedIncentiveListName(client.id, editingRow.id, trimmed)
      await mutate()
      setEditingRow(null)
      setEditingName('')
    } catch (e) {
      console.error('特典付与リスト名の変更に失敗しました', e)
      notifications.show({ message: 'リスト名の変更に失敗しました', color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: SavedIncentiveList) => {
    if (!confirmDelete(`「${row.name}」`)) return
    try {
      await deleteSavedIncentiveList(client.id, row.id)
      await mutate()
    } catch (e) {
      console.error('特典付与リストの削除に失敗しました', e)
      notifications.show({ message: '削除に失敗しました', color: 'red' })
    }
  }

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant="transparent">
          <FlagIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">特典付与リスト</Title>
      </Group>

      {error && <Text c="red">読み込みに失敗しました</Text>}
      {!error && !data && (
        <Center mt={20}>
          <Loader />
        </Center>
      )}
      {data && data.length === 0 && (
        <Text c="dimmed">保存されたリストはありません。CV一覧でリスト生成すると保存されます。</Text>
      )}
      {data && data.length > 0 && (
        <Box
          mt={16}
          style={{
            border: '1px solid #0000001A',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008', whiteSpace: 'nowrap' } }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '100%' }}>ファイル名</Table.Th>
                <Table.Th w={160}>作成日時</Table.Th>
                <Table.Th w={160} ta="center">詳細を表示</Table.Th>
                <Table.Th w={160} ta="center">ダウンロード</Table.Th>
                <Table.Th w={80} ta="center">編集</Table.Th>
                <Table.Th w={80} ta="center">削除</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.created_at)}</Table.Td>
                  <Table.Td ta="center">
                    <Anchor
                      fz={13}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleShowDetail(row)}
                    >
                      詳細を表示
                    </Anchor>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Anchor
                      fz={13}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleDownload(row)}
                    >
                      ダウンロード
                    </Anchor>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="ファイル名編集">
                      <ActionIcon variant="subtle" color="gray" onClick={() => openRenameModal(row)}>
                        <PencilSimpleIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="削除">
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(row)}>
                        <TrashIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      <BaseModal
        opened={editingRow !== null}
        onClose={closeRenameModal}
        title="ファイル名編集"
        width={500}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <TextInput
              ref={inputRef}
              label="ファイル名"
              value={editingName}
              onChange={(e) => setEditingName(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit()
              }}
              disabled={saving}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={closeRenameModal}
              disabled={saving}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              onClick={handleRenameSubmit}
              disabled={saving || editingName.trim() === ''}
              loading={saving}
            >
              保存
            </Button>
          </Group>
        </Box>
      </BaseModal>

      <IncentiveListSavedDetailModal
        opened={detailOpened}
        clientId={client.id}
        savedId={detailRow?.id ?? null}
        name={detailRow?.name ?? ''}
        onClose={() => setDetailOpened(false)}
        onExited={() => setDetailRow(null)}
      />
    </Box>
  )
}

export default IncentiveListSavedPage
